import { useState, useMemo, useCallback } from 'react';
import api from '../utils/api';

export interface ObjectImage {
  imageId: string;
  filename: string;
  s3Key: string;
  url?: string; // presigned URL for original
  visibility: 'public' | 'private';
  thumbnailUrl?: string; // smallest rendition URL (thumb > small > medium)
  displayUrl?: string; // largest rendition URL (large > medium > small) for main display
  // Internal-only per-image upload timestamp (issue #1112). Backend scrubs
  // this to null on public responses; null here means either (a) public viewer
  // or (b) legacy record from before the field existed.
  uploadedAt?: string | null;
}

export interface UseObjectImagesReturn {
  images: ObjectImage[];
  error: string | null;
  toggleVisibility: (imageId: string) => Promise<void>;
}

interface CatalogItemImage {
  imageId: string;
  filename: string;
  size: number;
  mimeType: string;
  s3Key: string;
  status: string;
  url?: string;
  visibility?: 'public' | 'private';
  uploadId?: string;
  uploadedAt?: string | null;
  renditions?: Array<{
    size: string;
    format: string;
    s3Key: string;
    dimensions?: { width: number; height: number };
    url?: string;
  }>;
}

export interface CatalogItemData {
  catalogItemId: string;
  coverImageId?: string;
  images: CatalogItemImage[];
}

/**
 * Get the best rendition URL for a given size preference order.
 * Falls back to the original URL if no rendition matches.
 */
function getBestRenditionUrl(img: CatalogItemImage, sizePreference: string[]): string | undefined {
  const originalUrl = img.url;

  if (!img.renditions || img.renditions.length === 0) {
    return originalUrl;
  }

  for (const preferredSize of sizePreference) {
    const rendition = img.renditions.find((r) => r.size === preferredSize);
    if (rendition?.url) {
      return rendition.url;
    }
  }

  // If no preferred size found, use first rendition with URL
  const firstRenditionWithUrl = img.renditions.find((r) => r.url);
  return firstRenditionWithUrl?.url || originalUrl;
}

/**
 * Get the best thumbnail URL from per-image renditions.
 * Prefers: thumb > small > medium > large > original URL
 */
export function getBestThumbnailUrl(img: CatalogItemImage): string | undefined {
  return getBestRenditionUrl(img, ['thumb', 'small', 'medium', 'large']);
}

/**
 * Get the best display URL from per-image renditions for main image display.
 * Prefers: large > medium > small > original URL (largest available for quality)
 */
function getBestDisplayUrl(img: CatalogItemImage): string | undefined {
  return getBestRenditionUrl(img, ['large', 'medium', 'small']);
}

/**
 * Hook for managing multi-image display and per-image visibility toggling.
 *
 * IMPORTANT: This hook does NOT fetch data separately. It derives images from
 * the catalogItemData provided by the caller to avoid duplicate API calls.
 *
 * @param catalogId - The catalog item ID
 * @param catalogItemData - The catalog item data already fetched by parent component
 * @returns Object with images array, loading state, error state, and toggleVisibility function
 */
export function useObjectImages(
  catalogId: string,
  catalogItemData: CatalogItemData | null
): UseObjectImagesReturn {
  const [error, setError] = useState<string | null>(null);
  const [visibilityOverrides, setVisibilityOverrides] = useState<Record<string, 'public' | 'private'>>({});

  // Derive base images from catalogItemData using useMemo (not useEffect + setState)
  const baseImages = useMemo<ObjectImage[]>(() => {
    if (!catalogItemData || !catalogItemData.images) {
      return [];
    }

    const mapped = catalogItemData.images.map((img) => ({
      imageId: img.imageId,
      filename: img.filename,
      s3Key: img.s3Key,
      url: img.url,
      visibility: img.visibility || 'private' as const,
      thumbnailUrl: getBestThumbnailUrl(img),
      displayUrl: getBestDisplayUrl(img),
      uploadedAt: img.uploadedAt ?? null,
    }));

    // Place the designated cover image first so it displays as the primary/hero image.
    // Falls back to original order when no coverImageId is set or the ID is not found.
    const { coverImageId } = catalogItemData;
    if (!coverImageId) return mapped;

    const coverIndex = mapped.findIndex((img) => img.imageId === coverImageId);
    if (coverIndex <= 0) return mapped; // Already first or not found — no change needed

    return [
      mapped[coverIndex],
      ...mapped.slice(0, coverIndex),
      ...mapped.slice(coverIndex + 1),
    ];
  }, [catalogItemData]);

  // Merge base images with optimistic visibility overrides
  // Maintains object identity for unchanged items (important for React reconciliation)
  const images = useMemo<ObjectImage[]>(() => {
    if (Object.keys(visibilityOverrides).length === 0) return baseImages;
    return baseImages.map((img) => {
      const override = visibilityOverrides[img.imageId];
      if (override !== undefined && override !== img.visibility) {
        return { ...img, visibility: override };
      }
      return img; // Keep same reference for unchanged items
    });
  }, [baseImages, visibilityOverrides]);

  /**
   * Toggle visibility for a specific image between public and private.
   * Updates the backend via API and updates local state immutably.
   */
  const toggleVisibility = useCallback(
    async (imageId: string): Promise<void> => {
      const image = images.find((img) => img.imageId === imageId);
      if (!image) {
        setError(`Image not found: ${imageId}`);
        return;
      }

      const newVisibility: 'public' | 'private' = image.visibility === 'public' ? 'private' : 'public';

      // Optimistically update UI via overrides
      setVisibilityOverrides((prev) => ({ ...prev, [imageId]: newVisibility }));
      setError(null);

      try {
        await api.patch(`/api/catalogs/${catalogId}/images/${imageId}/visibility`, {
          visibility: newVisibility,
        });
      } catch (err) {
        // Revert on error by removing the override
        setVisibilityOverrides((prev) => {
          const { [imageId]: _, ...rest } = prev;
          return rest;
        });
        const message = err instanceof Error ? err.message : 'Failed to update image visibility';
        setError(message);
      }
    },
    [catalogId, images]
  );

  return {
    images,
    error,
    toggleVisibility,
  };
}
